import { Link, Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  BookOpenText,
  Download,
  Languages as LanguagesIcon,
  Settings,
  Upload,
  UploadCloud,
  Users,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Spinner } from '@/components/ui/spinner';
import { Tabs } from '@/components/ui/tabs';
import { useProject } from '@/lib/api';

type ProjectTab =
  | 'settings'
  | 'translate'
  | 'glossary'
  | 'members'
  | 'import'
  | 'export'
  | 'publish';

const ICONS: Record<ProjectTab, typeof Settings> = {
  settings: Settings,
  translate: LanguagesIcon,
  glossary: BookOpenText,
  members: Users,
  import: Upload,
  export: Download,
  publish: UploadCloud,
};

export function ProjectDetailLayout() {
  const { wsId = '', projId = '' } = useParams<{ wsId: string; projId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const project = useProject(projId);

  const tab: ProjectTab = location.pathname.endsWith('/translate')
    ? 'translate'
    : location.pathname.endsWith('/glossary')
      ? 'glossary'
      : location.pathname.endsWith('/members')
        ? 'members'
        : location.pathname.endsWith('/import')
          ? 'import'
          : location.pathname.endsWith('/export')
            ? 'export'
            : location.pathname.endsWith('/publish')
              ? 'publish'
              : 'settings';

  const onTabChange = (next: ProjectTab) => {
    if (next === 'settings') void navigate(`/workspaces/${wsId}/projects/${projId}`);
    else void navigate(`/workspaces/${wsId}/projects/${projId}/${next}`);
  };

  return (
    <div className="space-y-6">
      <Link
        to={`/workspaces/${wsId}`}
        className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="size-3.5" aria-hidden />
        Back to workspace
      </Link>
      <header>
        {project.isLoading ? (
          <Spinner label="Loading project…" />
        ) : project.isError ? (
          <p
            role="alert"
            className="rounded-md border border-negative/40 bg-negative/10 p-3 text-sm text-negative"
          >
            Failed to load project: {project.error.message}
          </p>
        ) : project.data ? (
          <>
            <h1 className="text-2xl font-semibold tracking-tight">{project.data.name}</h1>
            <p className="mt-1 inline-flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
              <span>{project.data.slug}</span>
              <span aria-hidden>·</span>
              <Badge tone="primary">source: {project.data.sourceLanguageCode}</Badge>
              {(project.data.targetLanguageCodes ?? []).slice(0, 8).map((c) => (
                <Badge key={c}>{c}</Badge>
              ))}
              {(project.data.targetLanguageCodes ?? []).length > 8 && (
                <Badge>+{(project.data.targetLanguageCodes ?? []).length - 8}</Badge>
              )}
            </p>
          </>
        ) : null}
      </header>

      <Tabs<ProjectTab>
        value={tab}
        onChange={onTabChange}
        ariaLabel="Project sections"
        items={[
          { value: 'translate', label: tabLabel('translate', 'Translate') },
          { value: 'glossary', label: tabLabel('glossary', 'Glossary') },
          { value: 'settings', label: tabLabel('settings', 'Settings') },
          { value: 'members', label: tabLabel('members', 'Members') },
          { value: 'import', label: tabLabel('import', 'Import') },
          { value: 'export', label: tabLabel('export', 'Export') },
          { value: 'publish', label: tabLabel('publish', 'Publish') },
        ]}
      />
      <Outlet />
    </div>
  );
}

function tabLabel(tab: ProjectTab, label: string) {
  const Icon = ICONS[tab];
  return (
    <span className="inline-flex items-center gap-1.5">
      <Icon className="size-3.5" aria-hidden />
      {label}
    </span>
  );
}
